import { animated, useSpring } from "@react-spring/web";
import { toSnakeCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import { Spinner } from "@phosphor-icons/react";
import React from "react";
import FontAwesome from "react-fontawesome";
import { Info } from "../Info";
import styles from "./Panel.module.scss";
import { PanelProps } from "./types";

const cx = classNames.bind(styles);

const Panel = ({
  className = "",
  style,
  title,
  shouldShowTitleSubscript = true,
  titleSubscript,
  titleDecorator,
  ariaLabel = "Panel",
  description,
  loaded = true,
  onClick,
  children,
  iconName,
  actionRow,
  isFluidY = true,
  isHidden = false,
}: PanelProps) => {
  const springProps = useSpring({
    loop: true,
    delay: 0,
    from: {
      transform: "rotate(0deg)",
    },
    to: { transform: "rotate(360deg)" },
    config: {
      duration: 1000,
    },
  });
  const AnimatedSpinner = animated(Spinner);
  const isClickable = onClick && (loaded === undefined || loaded);
  const titleId = `${toSnakeCaseString(ariaLabel)}-title`;

  return (
    <div
      className={cx("container", className, {
        clickable: isClickable,
        isFluidY,
      })}
      style={style}
      onClick={isClickable ? onClick : () => null}
      hidden={isHidden}
      role="region"
      {...(title ? { "aria-labelledby": titleId } : {})}
      {...(ariaLabel && !title ? { "aria-label": ariaLabel } : {})}
    >
      <div
        className={cx("titleContainer", !title && !actionRow && "empty")}
        aria-label="title-container"
      >
        <div className={cx("titleWrapper")}>
          <div className={cx("titleContent")}>
            {iconName && (
              <FontAwesome
                className={cx("icon")}
                name={iconName}
              />
            )}

            {title && (
              <div
                className={cx("title")}
                id={titleId}
              >
                {title}
              </div>
            )}

            {shouldShowTitleSubscript && titleSubscript ? (
              <div className={cx("titleSubscript")}>{titleSubscript}</div>
            ) : null}
            {description ? <Info content={description} /> : null}
          </div>

          <div className={cx("titleDecorator")}>
            {titleDecorator ? titleDecorator : null}
          </div>
        </div>

        {actionRow ? (
          <div className={cx("actionsContainer")}>{actionRow}</div>
        ) : null}
      </div>

      {!loaded ? (
        <div className={cx("loaderContainer")}>
          <AnimatedSpinner
            size={25}
            style={springProps}
            className={cx("loader")}
          />
        </div>
      ) : (
        children
      )}
    </div>
  );
};

export default Panel;
